import { useCallback, useEffect, useMemo, useState } from "react";
import { InfoIcon } from "../../shared/ui/icons";
import { getMyBroadcasts, type ReaderBroadcast } from "../../shared/api/platform";

/**
 * Change 19 §4 — the reading end of a targeted broadcast.
 *
 * 🔴 IT IS NOT THE RELEASE-NOTICE STRIP THIS REPLACED, AND THE DIFFERENCE IS
 * THE WHOLE OF §5. That strip told schools a VERSION had shipped, which §5
 * removed outright — *"version updates are internal; no manual version
 * communication to schools"*. This carries what §4 asks for instead: a message
 * an operator deliberately addressed to Parents, Teachers, School Admins or a
 * named set of schools, about anything at all. The shape is the same because the
 * shape was right; the feature behind it is the one that was wanted.
 *
 * 🔴 IT SENDS NOTHING AND RECEIVES NOTHING PERSONAL. The role and the school
 * that decide which broadcasts come back are both read from the token, server
 * side; the response carries no audience, so a parent cannot learn that a
 * message went to every school except theirs.
 *
 * ⚠️ DISMISSAL IS `localStorage`, and the cost is the one the old strip named:
 * clearing a browser brings a broadcast back. That is a smaller failure than a
 * row per user per broadcast — a table that grows with every account, records a
 * reading habit, and answers a question nobody asks.
 *
 * ⚠️ THE KEY CARRIES THE BROADCAST ID, so a new one is never pre-dismissed by an
 * old dismissal — the failure a single `seen: true` flag would produce.
 */
const KEY = "kalqy.broadcast.dismissed";

function dismissed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [],
    );
  } catch {
    /* A corrupt or unavailable store is not a reason to hide a broadcast — and a
       throw here would take the whole shell down on a browser in private mode. */
    return new Set();
  }
}

function remember(id: string): void {
  try {
    const next = [...dismissed(), id];
    /* ⚠️ CAPPED. This list only ever grows, and an unbounded key in a store with
       a 5 MB budget is a slow leak; twenty ids is far more than the three that
       can be live at once. */
    window.localStorage.setItem(KEY, JSON.stringify(next.slice(-20)));
  } catch {
    /* Private mode. It reappears next load, which is the harmless half. */
  }
}

export interface PlatformBroadcastsProps {
  /**
   * The signed-in reader's bearer token.
   *
   * ⚠️ NO TOKEN, NO REQUEST. The role and school that decide which broadcasts
   * come back are read from the token and from nowhere else, so a signed-out
   * shell must not issue it at all.
   */
  token?: string;
  /** Something the reader can act on. Dismissing is the only action there is. */
  dismissLabel: string;
}

export function PlatformBroadcasts({ token, dismissLabel }: PlatformBroadcastsProps) {
  const [rows, setRows] = useState<ReaderBroadcast[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(() => dismissed());

  useEffect(() => {
    if (token === undefined) return;
    const controller = new AbortController();
    /*
     * 🔴 FAILURE IS SILENCE, NOT AN ERROR STATE. This strip sits above every
     * screen in the product; a dashboard rendering "couldn't load broadcasts"
     * above a teacher's class list would be worse than one rendering nothing,
     * and nothing else on the page depends on it.
     */
    getMyBroadcasts({ token, signal: controller.signal })
      .then(setRows)
      .catch(() => {
        setRows([]);
      });
    return () => {
      controller.abort();
    };
  }, [token]);

  const shown = useMemo(() => rows.filter((row) => !hidden.has(row.id)), [rows, hidden]);

  const dismiss = useCallback((id: string) => {
    remember(id);
    setHidden((current) => new Set(current).add(id));
  }, []);

  if (shown.length === 0) return null;

  /*
   * ⚠️ IT SITS INSIDE THE CONTENT COLUMN, NOT FULL-BLEED ACROSS THE SHELL, AND
   * THAT IS THE DIFFERENCE BETWEEN THIS AND THE IMPERSONATION BANNER. That one
   * is a warning about the session and spans everything on purpose; this is
   * something to read, so it takes the width of the thing you came to read.
   */
  return (
    <div className="mb-6 flex flex-col gap-2">
      {shown.map((row) => (
        <div
          key={row.id}
          role="status"
          className="flex flex-wrap items-start justify-between gap-3 rounded-lg px-4 py-3 font-dash text-dash-sm"
          style={{
            background: "var(--color-canvas)",
            color: "var(--color-ink)",
            border: "1px solid var(--color-line)",
          }}
        >
          <span className="flex items-start gap-2.5">
            <span aria-hidden="true" className="mt-0.5 inline-flex shrink-0 text-muted">
              <InfoIcon size={16} />
            </span>
            <span>
              <span className="font-bold">{row.title}</span>{" "}
              <span className="text-ink-soft">{row.body}</span>{" "}
              {/* ⚠️ A LABEL, NEVER A PERSON. `Announcement.authorLabel` sets the
                  constraint: never a phone number and never an email. A reader
                  learns that Kalqy is speaking, not which operator typed it. */}
              <span className="text-muted">— {row.authorLabel}</span>
            </span>
          </span>
          {/*
            ⚠️ A REAL BUTTON WITH A REAL LABEL, NOT AN `×`. This sits above every
            screen a signed-in adult opens; an unlabelled glyph in the top-right
            of a page is the shape people click without reading, and the thing
            they are dismissing is the only place this message appears.
          */}
          <button
            type="button"
            className="shrink-0 rounded-lg px-2 py-1 text-dash-xs font-bold text-muted underline transition-colors duration-150 ease-dash hover:text-ink"
            onClick={() => {
              dismiss(row.id);
            }}
          >
            {dismissLabel}
          </button>
        </div>
      ))}
    </div>
  );
}
